import { HistoryMode } from "src/clients/flow-api";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import { useWorkspaceContext } from "src/router/routerContextHooks";

/**
 * Hook to determine if long test runs can run on Iceberg
 *
 * Returns true if either:
 * - The workspace config `enable_dhv2_long_test_runs` is enabled AND the
 *   history_mode writes to Iceberg (any mode except write_aurora_read_aurora), OR
 * - Falls back to the existing `useIsIcebergEnabled()` behavior
 *
 * Long test run reads are scoped to the job run (job_run_id + run start time),
 * so they only need Iceberg writes to be enabled — not the migration backfill
 * that the global read_iceberg signal implies.
 */
export const useIsIcebergLongTestRunsEnabled = (): boolean => {
  const { workspace } = useWorkspaceContext();
  const isIcebergEnabled = useIsIcebergEnabled();
  const historyMode = workspace.config?.history_mode;

  // enable_dhv2_long_test_runs is only effective when the workspace writes to
  // Iceberg. AURORA_READ_AURORA is the only mode that doesn't write to Iceberg.
  if (workspace.config?.enable_dhv2_long_test_runs) {
    return (
      historyMode !== undefined &&
      historyMode !== HistoryMode.AURORA_READ_AURORA
    );
  }

  // Fall back to existing iceberg behavior
  return isIcebergEnabled;
};
